/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {loadDevices} from '@ion/core/src/services/Connect';
import {Device} from '@ion/core/src/types/entities';
import {createMutationResult} from '../utils/helpers';

export interface UseDevicesDropdownParams {
  accountId: number;
  pageId: number;
  pageSize: number;
  searchTerms: string;
};

export type UseDevicesDropdownResult = {
  device: Device;
  loadDevice: (params: UseDevicesDropdownParams, callbacks) => void;
}

/**
 * The useDevicesDropdown hook is used to retrieve devices.
 *
 * ```typescript
 *
 *  import {useDevicesDropdown} from '@ion/packages/query/src/hooks';
 *
 *  const {device} = useDevicesDropdown({
 *    accountId,
 *    pageId,
 *    pgSize,
 *    searchTerms
 *  });
 *
 * ```
 */
export const useDevicesDropdown = (): UseDevicesDropdownResult => {
  const mutation = useMutation(async(params: UseDevicesDropdownParams) => {
    const {
      accountId,
      pageId,
      pageSize,
      searchTerms
    } = params;
    const {devices: items} = await loadDevices({
      pageId   : pageId,
      pageSize : pageSize,
      sortOn   : ['+name'],
      filterOn : searchTerms?.length ? {
        name : `*${searchTerms?.toLowerCase()}*`
      } : null,
      accountId
    });
    return items;
  });

  return createMutationResult(
    mutation, {
      loadDevice : mutation.mutate
    }
  ) as UseDevicesDropdownResult;
};

export default useDevicesDropdown;
